import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Container } from "react-bootstrap";
import {
  getRemnants,
  RemnatsProps,
  patchRemnants,
  updateMPData,
  retireMp,
} from "../api/ProductRemnants";
import { getAllFeatureFlags } from "../api/Features";
import {
  getDinamicRemnant,
  getEval,
  getSubRowsData,
  RemnantRowProps,
  PrepareRemnantRowProps,
  DisabledSend,
  lastSendTs,
} from "./common";
import FullRemnants from "./FullRemnants";
import { TableTitle } from "./RemnantsStyled";

const MIN_THRESHOLD = 0;

export type RemnantsSendingFlags = {
  wb: boolean;
  ozon: boolean;
  ya: boolean;
  sber: boolean;
  ali: boolean;
};

const defaultRemnantsSendingFlags: RemnantsSendingFlags = {
  wb: true,
  ozon: true,
  ya: false,
  sber: false,
  ali: false,
};

const Remnants = () => {
  const pendingUpdate = useRef(false);
  const [remnants, setRemnants] = useState<PrepareRemnantRowProps[]>([]);
  const [remnantsSendingFlags, setRemnantsSendingFlags] =
    useState<RemnantsSendingFlags>(defaultRemnantsSendingFlags);
  const [retirementEnabled, setRetirementEnabled] = useState(false);

  const loadRemnants = useCallback(async () => {
    const result = await getRemnants();
    const combos = result.filter((el) => el.is_combo);
    const articlesUseInCombo = Object.fromEntries(
      combos
        .map((el) =>
          el.comboComposition
            ? el.comboComposition.map((el) => el.article)
            : []
        )
        .flat()
        .map((el) => [el, el])
    );
    setRemnants(
      result.map((el) => ({
        ...el,
        ...el.product_remnants,
        useInCombo: !!articlesUseInCombo[el.article],
      }))
    );
  }, []);

  useEffect(() => {
    loadRemnants();
  }, [loadRemnants]);

  useEffect(() => {
    const loadFlags = async () => {
      try {
        const flags = await getAllFeatureFlags();
        const map = new Map(flags.map((el) => [el.name, el.value]));
        setRemnantsSendingFlags({
          wb: map.get("wb_remnants_sending") !== false,
          ozon: map.get("ozon_remnants_sending") !== false,
          ya: false,
          sber: false,
          ali: false,
        });
        setRetirementEnabled(false);
      } catch (e) {
        console.log("Failed to load remnants sending flags", e);
      }
    };
    loadFlags();
  }, []);

  const updateMpNeedUpdate = async (
    rows: {
      article: string;
      wb_data?: { need_update_stock: boolean };
      ozon_data?: { need_update_stock: boolean };
      ya_data?: { need_update_stock: boolean };
      sber_data?: { need_update_stock: boolean };
      ali_data?: { need_update_stock: boolean };
    }[]
  ) => {
    if (!rows.length) return false;
    if (pendingUpdate.current) return false;
    pendingUpdate.current = true;
    try {
      await updateMPData(rows);
      await loadRemnants();
      return true;
    } catch (e) {
      console.log(e);
      return false;
    } finally {
      pendingUpdate.current = false;
    }
  };

  const onRetire = async (
    article: string,
    marketplace: "wildberries" | "ozon",
    action: "retire" | "return"
  ) => {
    if (pendingUpdate.current) return false;
    pendingUpdate.current = true;
    try {
      await retireMp([{ article, marketplace, action }]);
      await loadRemnants();
      return true;
    } catch (e) {
      console.log(e);
      return false;
    } finally {
      pendingUpdate.current = false;
    }
  };

  const calculatedRemnants = useMemo(() => {
    return remnants.map((el) => {
      const subRows = getSubRowsData({
        comboComposition: el.comboComposition,
        remnants: remnants,
      });
      const { remnantToCombo, remnantForSingle } = getDinamicRemnant(el);
      const rowNotError = subRows.every((el) => el.finded);
      let remnantValue = el.is_combo
        ? el.comboComposition?.length && rowNotError
          ? Math.min(...subRows.map((el) => el.remnantDevValue))
          : 0
        : el.useInCombo
        ? remnantForSingle
        : remnantForSingle +
          (typeof remnantToCombo === "number" ? remnantToCombo : 0);
      remnantValue = remnantValue <= MIN_THRESHOLD ? 0 : remnantValue;
      const totalReserved =
        (el.reserved_in_wildberries ?? 0) +
        (el.reserved_in_ozon ?? 0) +
        (el.reserved_in_ya ?? 0) +
        (el.reserved_in_sber ?? 0) +
        (el.reserved_in_ali ?? 0) +
        (el.reserved_in_combo ?? 0);
      return {
        ...el,
        subRows,
        remnant: el.is_combo ? remnantValue : el.remnant,
        remnantValue,
        totalReserved,
        rowNotError,
        remnantToCombo,
        remnantForSingle,
        calculeted_wildberries: getEval(el.share_in_wildberries, remnantValue),
        calculeted_ozon: getEval(el.share_in_ozon, remnantValue),
        calculeted_ya: getEval(el.share_in_ya, remnantValue),
        calculeted_sber: getEval(el.share_in_sber, remnantValue),
        calculeted_ali: getEval(el.share_in_ali, remnantValue),
        disabled_send_to_wildberries: el.wb_data?.disabled_for_send ?? false,
        disabled_send_to_ozon: el.ozon_data?.disabled_for_send ?? false,
        disabled_send_to_ya: el.ya_data?.disabled_for_send ?? false,
        disabled_send_to_sber: el.sber_data?.disabled_for_send ?? false,
        disabled_send_to_ali: el.ali_data?.disabled_for_send ?? false,
        ozon_quarantine_at: el.ozon_data?.quarantine_at ?? null,
        wb_quarantine_at: el.wb_data?.quarantine_at ?? null,
        last_send_wb_at: lastSendTs(el.wb_data?.last_update),
        last_send_ozon_at: lastSendTs(el.ozon_data?.last_update),
        last_send_ya_at: lastSendTs(el.ya_data?.last_update),
        last_send_sber_at: lastSendTs(el.sber_data?.last_update),
        last_send_ali_at: lastSendTs(el.ali_data?.last_update),
      };
    });
  }, [remnants]);

  const update = async (
    props: {
      article: string;
      value: Partial<RemnatsProps["product_remnants"]>;
    }[]
  ) => {
    if (pendingUpdate.current) return false;
    pendingUpdate.current = true;
    try {
      await patchRemnants(
        props.map((el) => ({ article: el.article, data: el.value }))
      );

      setRemnants(
        remnants.map((el) => {
          const data = props.find((pr) => pr.article === el.article);
          if (!data) return el;
          console.log(data);
          return {
            ...el,
            ...data.value,
          };
        })
      );
    } catch (e) {
      console.log(e);

      pendingUpdate.current = false;
      return false;
    }
    pendingUpdate.current = false;
    return true;
  };

  const updateDisabledSend = async (
    props: {
      article: string;
      value: Partial<DisabledSend>;
    }[]
  ) => {
    if (pendingUpdate.current) return false;
    pendingUpdate.current = true;
    try {
      const forUpdate = props.map((el) => ({
        article: el.article,
        ...(typeof el.value.disabled_send_to_wildberries === "boolean" ||
        typeof el.value.wb_quarantine_at !== "undefined"
          ? {
              wb_data: {
                ...(typeof el.value.disabled_send_to_wildberries === "boolean"
                  ? { disabled_for_send: el.value.disabled_send_to_wildberries }
                  : {}),
                ...(typeof el.value.wb_quarantine_at !== "undefined"
                  ? { quarantine_at: el.value.wb_quarantine_at }
                  : {}),
              },
            }
          : {}),

        ...(typeof el.value.disabled_send_to_ozon === "boolean"
          ? {
              ozon_data: {
                disabled_for_send: el.value.disabled_send_to_ozon,
                ...(typeof el.value.ozon_quarantine_at !== "undefined"
                  ? { quarantine_at: el.value.ozon_quarantine_at }
                  : {}),
              },
            }
          : {}),
        ...(typeof el.value.disabled_send_to_ozon !== "boolean" &&
        typeof el.value.ozon_quarantine_at !== "undefined"
          ? {
              ozon_data: {
                quarantine_at: el.value.ozon_quarantine_at,
              },
            }
          : {}),

        ...(typeof el.value.disabled_send_to_ya === "boolean"
          ? {
              ya_data: {
                disabled_for_send: el.value.disabled_send_to_ya,
              },
            }
          : {}),

        ...(typeof el.value.disabled_send_to_sber === "boolean"
          ? {
              sber_data: {
                disabled_for_send: el.value.disabled_send_to_sber,
              },
            }
          : {}),

        ...(typeof el.value.disabled_send_to_ali === "boolean"
          ? {
              ali_data: {
                disabled_for_send: el.value.disabled_send_to_ali,
              },
            }
          : {}),
      }));
      await updateMPData(forUpdate);
      const updateData = remnants.map((el) => {
        const data = forUpdate.find((pr) => pr.article === el.article);
        if (!data) return el;
        const nextOzonData = data.ozon_data
          ? {
              ...(el.ozon_data || { disabled_for_send: false }),
              ...data.ozon_data,
            }
          : el.ozon_data;
        const nextWbData = (data as any).wb_data
          ? {
              ...(el.wb_data || { disabled_for_send: false }),
              ...(data as any).wb_data,
            }
          : el.wb_data;
        return {
          ...el,
          ...data,
          ozon_data: nextOzonData,
          wb_data: nextWbData,
        };
      });
      console.log("updateData", updateData);
      setRemnants(updateData);
    } catch (e) {
      console.log(e);

      pendingUpdate.current = false;
      return false;
    }
    pendingUpdate.current = false;
    return true;
  };

  return (
    <Container>
      <TableTitle>Товары</TableTitle>
      <FullRemnants
        remnants={calculatedRemnants}
        update={update}
        updateDisabledSend={updateDisabledSend}
        reloadRemnants={loadRemnants}
        updateMpNeedUpdate={updateMpNeedUpdate}
        remnantsSendingFlags={remnantsSendingFlags}
        retirementEnabled={retirementEnabled}
        onRetire={onRetire}
      />
    </Container>
  );
};

export default Remnants;
